import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const RedDeer = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const neighborhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e9%98%bf%e5%b0%94%e4%bc%af%e5%a1%94%e7%9c%81-%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/alberta-neighbourhoods/'

  const negotiateRentLink = isChinese
    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e4%b8%8e%e6%88%bf%e4%b8%9c%e5%95%86%e8%ae%a8%e7%a7%9f%e9%87%91%e7%9a%846%e4%b8%aa%e6%8a%80%e5%b7%a7/'
    : 'https://liv.rent/blog/renters/how-to-negotiate-rent/'

  const getAbout = () => {
    return (
      <Trans i18nKey="seo.city.red-deer:About Red Deer Paragraph 1">
        <Paragraph>
          Red Deer, located in central{' '}
          <LinkText href="/rental-listings/alberta">Alberta</LinkText>, serves
          as an important hub situated almost equidistant from both{' '}
          <LinkText href="/rental-listings/city/edmonton">Edmonton</LinkText>{' '}
          and <LinkText href="/rental-listings/city/calgary">Calgary</LinkText>.
          The city boasts a diverse economy, with strengths in sectors such as
          oil and gas, agriculture, manufacturing, and various service
          industries. For outdoor enthusiasts, Red Deer offers extensive
          recreational options, prominently featured through the extensive
          trails and natural spaces of the Waskasoo Park system. Cultural
          pursuits can be enjoyed at various venues around the city, including
          museums and art galleries, and through programs offered by Red Deer
          College. The city’s community spirit shines during numerous local
          events, with the Westerner Days Fair & Exposition being one of the
          most anticipated annual celebrations. Home to approximately 100,000
          residents, Red Deer emerges as a significant urban center in Alberta,
          promoting engagement in education, sports, and the arts, made readily
          accessible due to its strategic positioning along major transportation
          routes.
        </Paragraph>
      </Trans>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={neighborhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$900 - $1,700',
          bed2: '$1,100 - $2,100',
          bed3: '$1,400 - $2,400'
        }}
        rentReportCity={t('states:Alberta')}
        rentReportLink="https://liv.rent/blog/rent-reports/alberta/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.red-deer:Apartment Hunting in Red Deer Paragraph 1">
            Finding affordable rentals in Red Deer is easier compared to other
            major cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and <LinkText href="/rental-listing/city/toronto">Toronto</LinkText>
            , especially if you’re looking for{' '}
            <LinkText href={links.under1000}>rentals under $1000</LinkText>.
            Along with effective negotiation skills, you may be able to lower
            the rent price further. For helpful tips on negotiating rent, refer
            to our guide on{' '}
            <LinkText href={negotiateRentLink}>how to negotiate rent</LinkText>.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.red-deer:Apartment Hunting in Red Deer Paragraph 2">
            Red Deer offers a diverse range of neighborhoods and municipalities
            to choose from when searching for rentals. Depending on your
            preferences, you can explore various rental types, such as{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>, and
            <LinkText href={links.studio}>studio apartments</LinkText>. Whether
            you’re seeking{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> or{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText>,
            or even considering
            <LinkText href={links.longTerm}>long term</LinkText> or{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>, Red
            Deer has a variety of choices to suit your needs.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.red-deer:Apartments for rent Red Deer Paragraph">
              <LinkText href={links.apartment}>Apartments for rent</LinkText>{' '}
              are the most abundant rental type in Red Deer, especially in the
              downtown area and around Red Deer College. These are suitable for
              students, single professionals, and young couples. Monthly rents
              can vary significantly based on unit size and condition.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.red-deer:Houses for rent Red Deer Paragraph">
              Full <LinkText href={links.house}>houses for rent</LinkText> can
              be found in virtually all residential neighborhoods across Red
              Deer, such as Anders Park, Sunnybrook, or Mountview. Typically,
              they are more suitable for larger families or groups who are
              willing to share. Both partial houses and entire houses for rent
              are common, with a mix of older and newer homes depending on which
              part of the city you’re looking in.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Townhouses for rent LOCATION Paragraph">
              Townhouses are another popular form of housing in {{ cityName }}{' '}
              that combine the best qualities of both apartments and whole
              houses. Typically less expensive than houses,{' '}
              <LinkText href={links.townhouse}>townhouses for rent</LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.red-deer:Basements for rent Alberta Paragraph">
              A popular alternative for students,{' '}
              <LinkText href="https://liv.rent/blog/livrent/top-tips-for-first-time-renters/">
                first-time renters
              </LinkText>{' '}
              and anyone else seeking relatively inexpensive rental housing are
              basements for rent in {{ cityName }}. Since rent prices for
              apartments and houses can often get too expensive, many renters
              seek out basements for rent in {{ cityName }} as a cheaper
              alternative. Homeowners often rent out their basements as a way to
              offset their mortgage payments. This makes it easy for renters to
              find relatively inexpensive basements for rent in all areas of the
              city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.red-deer:Condos for rent Alberta Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Red Deer
              </LinkText>{' '}
              are scattered throughout the city, with a concentration in upscale
              neighborhoods or newer developments. These types of rental
              accommodation are ideal for professionals and small families
              seeking modern amenities. If you are looking to rent in the city
              centre though, be prepared for higher monthly rent prices.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.red-deer:Short term rentals Red Deer Paragraph">
              Red Deer, while not Canada’s largest city, holds a strategic
              position in Alberta and presents various employment possibilities,
              primarily driven by the oil and gas, agriculture, manufacturing,
              and service sectors. Though it may not be home to world-class
              academic institutions, it does host solid educational facilities,
              including Red Deer College, which attracts a number of students
              and educators. This dynamic creates a demand for{' '}
              <LinkText href={links.shortTerm}>short-term rentals</LinkText>,
              especially evident during key academic enrollment periods and the
              summer months when seasonal workers often migrate to the city. The
              added flexibility is a key factor for renters in
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are suitable
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.red-deer:Cheap rentals Red Deer Paragraph">
              Compared to other Canadian cities, rent prices in Red Deer are
              relatively cheaper. As a result, it shouldn’t be too hard to find
              something within your budget. If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Red Deer, AB
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in Red Deer where
              rent is considerably cheaper, so exploring these options is a good
              idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.red-deer:Living in Red Deer Paragraph">
          Red Deer, <LinkText href="/rental-listings/alberta">Alberta</LinkText>
          , offers a balanced blend of urban amenities and the tranquility of a
          medium-sized city, making it an appealing place for families, singles,
          and retirees alike. The city provides a variety of affordable housing
          options, catering to students, working professionals, and families.
          Residents and visitors alike cherish the city’s expansive park system,
          including the notable Waskasoo Park, which offers numerous
          recreational activities from hiking and biking in the summer to
          cross-country skiing in the winter. Red Deer takes pride in its
          vibrant cultural scene, underlined by various festivals, performing
          arts, and the notable Alberta Sports Hall of Fame. The city’s
          strategic location between Edmonton and Calgary, combined with its
          burgeoning local economy — underscored by sectors like oil and gas,
          manufacturing, and agriculture — marks it as a dynamic hub within
          central Alberta.
        </Trans>
      </Paragraph>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.red-deer:Popular Neighbourhoods Red Deer Paragraph">
          Like any city, Red Deer is made up of smaller neighbourhoods with
          their own distinct features. Some of the most popular neighbourhoods
          include Downtown Red Deer, Riverside Meadows, West Park, Normandeau,
          Sunnybrook, Inglewood, Johnstone Park, Eastview Estates, Deer Park,
          and Kentwood. For more info, you can refer to our blog on{' '}
          <LinkText href={neighborhoodsLink}>Alberta neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.red-deer:Local Landmarks Red Deer Paragraph">
          Red Deer, Alberta, is sprinkled with landmarks that capture its
          cultural heritage and love for the outdoors. A standout is the
          Waskasoo Park system, offering an extensive network of trails and
          green spaces, and including sites like the Kerry Wood Nature Centre
          and the historic Fort Normandeau, depicting the city’s rich past. The
          Alberta Sports Hall of Fame and Museum pays tribute to the province’s
          sporting legends, offering interactive exhibits and a celebration of
          athletic achievements. The Cronquist House, a beautifully preserved
          Victorian-style farmhouse, sits as a cultural beacon in Bower Ponds
          Recreation Area, hosting various community events and festivities.
          Moreover, the Red Deer Mountain Bike Park showcases the city’s
          commitment to outdoor sports and recreation, providing a haven for
          adventure enthusiasts amidst the urban setting.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default RedDeer
